/**
 * Cart drawer complementary products ("Pair with" / "You may also like").
 *
 * Loaded globally from layout/theme.liquid (deferred, classic script — not a
 * module) so it is guaranteed to run even though the markup it operates on
 * arrives inside dynamically re-rendered cart drawer HTML. It defines a
 * single custom element, <cart-complementary-products>, whose
 * connectedCallback re-fires automatically every time Dawn swaps in fresh
 * drawer HTML via innerHTML (CartDrawer.renderContents /
 * CartDrawerItems.updateQuantity) — the same mechanism Dawn already relies
 * on for <quantity-popover>, <cart-remove-button>, etc. No manual
 * re-initialization code is required.
 */
(function () {
  'use strict';

  var ADD_BUTTON_SELECTOR = '[data-complementary-add-button]';

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function getCartErrorsElement() {
    return document.getElementById('CartDrawer-CartErrors');
  }

  function reportError(context, error) {
    console.error('[cart-complementary-products] ' + context, error);
  }

  class CartComplementaryProducts extends HTMLElement {
    connectedCallback() {
      this._destroyed = false;
      this._onClick = this.handleClick.bind(this);
      this.addEventListener('click', this._onClick);

      var url = this.dataset.url;
      var productId = this.dataset.productId;
      var sectionId = this.dataset.sectionId || 'cart-complementary-products';
      var limit = this.dataset.limit || 6;

      // No source product (empty cart) or missing config — nothing to show.
      if (!url || !productId) {
        this.remove();
        return;
      }

      this.renderLoading();
      // Try Shopify's algorithmic "related" recommendations first; if none
      // come back, fall back to manually-assigned complementary products
      // instead of hiding the block outright.
      this.loadRecommendations(url, productId, sectionId, limit, 'related');
    }

    buildUrl(url, productId, sectionId, limit, intent) {
      return (
        url +
        (url.indexOf('?') > -1 ? '&' : '?') +
        'product_id=' +
        encodeURIComponent(productId) +
        '&section_id=' +
        encodeURIComponent(sectionId) +
        '&limit=' +
        encodeURIComponent(limit) +
        '&intent=' +
        encodeURIComponent(intent)
      );
    }

    disconnectedCallback() {
      if (this._onClick) this.removeEventListener('click', this._onClick);
      this._destroyed = true;
    }

    renderLoading() {
      var loadingText =
        (window.accessibilityStrings && window.accessibilityStrings.loading) || 'Loading recommendations…';
      this.innerHTML = '<p class="cart-drawer__complementary-products-loading">' + loadingText + '</p>';
    }

    loadRecommendations(url, productId, sectionId, limit, intent) {
      var self = this;
      var requestUrl = this.buildUrl(url, productId, sectionId, limit, intent);

      fetch(requestUrl)
        .then(function (response) {
          if (!response.ok) {
            throw new Error('Recommendations request failed with status ' + response.status);
          }
          return response.text();
        })
        .then(function (text) {
          if (self._destroyed) return;

          var wrapper = document.createElement('div');
          wrapper.innerHTML = text;
          var fetched = wrapper.querySelector('cart-complementary-products');

          if (!fetched || !fetched.querySelector('[data-complementary-card]')) {
            if (intent === 'related') {
              // No algorithmic related products came back — fall back to
              // manually-assigned complementary products instead of hiding
              // the block outright.
              self.loadRecommendations(url, productId, sectionId, limit, 'complementary');
              return;
            }

            // Still nothing (no related AND no complementary products) —
            // remove the block entirely instead of leaving an empty gap or
            // stuck loading text.
            self.remove();
            return;
          }

          self.innerHTML = fetched.innerHTML;
        })
        .catch(function (error) {
          reportError('Failed to load ' + intent + ' recommendations.', error);

          if (intent === 'related' && !self._destroyed) {
            // The related request itself failed (network/5xx) — still worth
            // trying the complementary fallback before giving up.
            self.loadRecommendations(url, productId, sectionId, limit, 'complementary');
            return;
          }

          // Never leave "Loading recommendations..." on screen.
          if (!self._destroyed) self.remove();
        });
    }

    handleClick(event) {
      var button = event.target.closest(ADD_BUTTON_SELECTOR);
      if (!button || !this.contains(button)) return;

      event.preventDefault();
      this.addToCart(button);
    }

    addToCart(button) {
      if (button.getAttribute('aria-disabled') === 'true') return;

      var variantId = button.dataset.variantId;
      if (!variantId) {
        reportError('Add button is missing a variant id.', button);
        return;
      }

      if (!window.routes || !window.routes.cart_add_url) {
        reportError('window.routes.cart_add_url is not defined.', window.routes);
        return;
      }

      var cartDrawer = getCartDrawer();
      var sectionsToRender =
        cartDrawer && typeof cartDrawer.getSectionsToRender === 'function' ? cartDrawer.getSectionsToRender() : [];

      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');

      var requestBody = {
        items: [{ id: variantId, quantity: 1 }],
      };

      if (sectionsToRender.length) {
        requestBody.sections = sectionsToRender.map(function (section) {
          return section.id;
        });
        requestBody.sections_url = window.location.pathname;
      }

      fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(requestBody),
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          if (data.status) {
            var message = data.description || data.message || (window.cartStrings && window.cartStrings.error);
            reportError('Add to cart returned an error.', data);
            var errorsElement = getCartErrorsElement();
            if (errorsElement) errorsElement.textContent = message;
            return;
          }

          if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
            // Repaints #CartDrawer (items, subtotal, footer) and the cart
            // icon bubble, then opens the drawer if it wasn't already open.
            // This replaces the current <cart-complementary-products>
            // instance with a fresh one, which re-fetches automatically.
            cartDrawer.renderContents(data);
          } else {
            // No cart drawer on this page/theme state — fall back to a full
            // cart page redirect rather than leaving the UI stale.
            window.location = window.routes.cart_url;
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-complementary-products',
              productVariantId: variantId,
              cartData: data,
            });
          }
        })
        .catch(function (error) {
          reportError('Network error while adding complementary product to cart.', error);
          var errorsElement = getCartErrorsElement();
          if (errorsElement && window.cartStrings) errorsElement.textContent = window.cartStrings.error;
        })
        .finally(function () {
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
        });
    }
  }

  if (!customElements.get('cart-complementary-products')) {
    customElements.define('cart-complementary-products', CartComplementaryProducts);
  }
})();
